import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, DownloadCloud, KeyRound, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const load = async () => {
      try {
        const r = await api.get(`/orders/${orderId}`);
        if (cancelled) return null;
        setOrder(r.data);
        return r.data;
      } catch {
        if (!cancelled) setOrder(null);
        return null;
      }
    };

    const reconcile = async () => {
      try {
        const r = await api.post(`/orders/${orderId}/reconcile`);
        if (cancelled) return null;
        setOrder(r.data);
        return r.data;
      } catch {
        return null;
      }
    };

    const run = async () => {
      const first = await load();
      setLoading(false);
      if (!first || first.status === "paid" || first.provider !== "pesapal") return;
      // Buyer just returned from PesaPal — confirm status directly a few times
      // in case the IPN hasn't landed yet.
      setReconciling(true);
      const tick = async () => {
        attempts += 1;
        const upd = await reconcile();
        if (cancelled) return;
        if (upd?.status === "paid" || attempts >= 6) {
          setReconciling(false);
          return;
        }
        setTimeout(tick, 3000);
      };
      tick();
    };

    run();
    return () => { cancelled = true; };
  }, [orderId]);

  if (loading) return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;
  if (!order) return <div className="p-24 text-center text-emerald-100/50">Order not found.</div>;

  const paid = order.status === "paid";

  return (
    <div className="mx-auto max-w-3xl px-5 py-16 lg:py-24">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-moss relative overflow-hidden p-8 lg:p-12"
      >
        <div className="flex items-center justify-between">
          <p className="overline">{paid ? "Order · paid" : "Order · " + order.status}</p>
          <span className={`chip ${paid ? "chip-emerald" : "chip-amber"}`}>{order.status}</span>
        </div>
        <h1 data-testid="order-success-title" className="mt-4 font-editorial text-4xl font-medium text-white sm:text-5xl">
          {paid ? (<>Payment received. <span className="italic text-emerald-300">You’re in.</span></>) : "Order created"}
        </h1>
        <p className="mt-4 max-w-lg text-sm text-emerald-100/70">
          {paid
            ? "Your licenses will appear in your dashboard within a moment. Bind an MT4/MT5 account to any Expert Advisor from there."
            : reconciling
              ? "Confirming your payment with the provider — this can take a few seconds. Please keep this page open."
              : "This order is not yet paid. Return to the checkout to complete or start a new one. If you already paid, wait a moment or contact support with your reference below."}
        </p>

        <div className="mt-6 divide-y divide-emerald-500/10 rounded-xl border border-emerald-500/15 bg-[#0d2416]/50">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm font-medium text-white">{i.title}</p>
                <p className="text-xs text-emerald-100/50">Qty · {i.quantity}</p>
              </div>
              <p className="font-editorial text-lime-300">${(i.price * i.quantity).toFixed(2)}</p>
            </div>
          ))}
          <div className="flex items-center justify-between p-4">
            <p className="text-sm text-emerald-100/70">Total paid</p>
            <p className="font-editorial text-2xl text-white">${order.total.toFixed(2)} <span className="text-xs text-emerald-100/50">{order.currency}</span></p>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/50 p-4">
            <DownloadCloud size={14} className="text-lime-300" />
            <p className="mt-2 text-xs text-emerald-100/60">Delivery</p>
            <p className="text-sm text-white">Available in dashboard</p>
          </div>
          <div className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/50 p-4">
            <KeyRound size={14} className="text-lime-300" />
            <p className="mt-2 text-xs text-emerald-100/60">License</p>
            <p className="text-sm text-white">MT4/MT5 binding on request</p>
          </div>
          <div className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/50 p-4">
            <CheckCircle2 size={14} className="text-lime-300" />
            <p className="mt-2 text-xs text-emerald-100/60">Reference</p>
            <p className="font-mono-edge text-xs text-white">{order.external_ref}</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/dashboard" className="btn-edge">Open dashboard <ArrowUpRight size={14} /></Link>
          <Link to="/store/expert-advisors" className="btn-edge-ghost">Keep browsing</Link>
        </div>
      </motion.div>
    </div>
  );
}
